/* CountdownPill — live 48h expiry countdown for pending duel cards.
 * Ticks once per minute; auto-remounts on unmount. Colour shifts to
 * amber < 6h and rose < 1h so time pressure is visible at a glance.
 */
import { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';

function fmt(msRemaining) {
  if (msRemaining <= 0) return 'expired';
  const totalMin = Math.floor(msRemaining / 60_000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h >= 24) {
    const d = Math.floor(h / 24);
    return `${d}d ${h % 24}h`;
  }
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${m}m`;
}

export default function CountdownPill({ expiresAt, testId }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  const remaining = new Date(expiresAt).getTime() - now;
  const label = fmt(remaining);
  const isExpired = remaining <= 0;
  const isUrgent = remaining > 0 && remaining < 3_600_000; // < 1h
  const isWarn = remaining > 0 && remaining < 6 * 3_600_000; // < 6h

  const tone = isExpired
    ? 'bg-neutral-800/60 border-neutral-700 text-neutral-500'
    : isUrgent
    ? 'bg-rose-500/15 border-rose-400/40 text-rose-300 animate-pulse'
    : isWarn
    ? 'bg-amber-500/15 border-amber-400/40 text-amber-300'
    : 'bg-emerald-500/10 border-emerald-400/25 text-emerald-300';

  return (
    <span
      data-testid={testId || 'countdown-pill'}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono border tabular-nums ${tone}`}
      title={new Date(expiresAt).toLocaleString()}
    >
      <Clock className="w-2.5 h-2.5" />
      {isExpired ? 'expired' : `Expires in ${label}`}
    </span>
  );
}
